import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'
import {v4 as uuid} from 'uuid'

import apiService from 'src/services/api.service'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'
import {useToggleState} from 'src/utils/hooks/useToggleState'

import s from './Chat.module.scss'
import ChatButton from './ChatButton'
const cx = cn.bind(s)

const ChatWindow = dynamic(() => import('./ChatWindow'))

const Chat: FC = () => {
	const [isOpened, toggleIsOpened] = useToggleState(false)
	const [isDirty, setIsDirty] = useState(false)
	const authStore = useAuthStore()
	const [initialMessages, setInitialMessages] = useState<
		Api.chatHistory.TVercelChatMessage[] | undefined
	>(undefined)
	const [chatId, setChatId] = useState<string>('')
	const buttonRef = useRef<HTMLButtonElement>(null)
	const router = useRouter()

	useEffect(() => {
		const resizeHandler = () => {
			const chatHeight = window.innerHeight
			document.documentElement.style.setProperty('--chat-h', `${chatHeight}px`)
		}
		resizeHandler()
		window.addEventListener('resize', resizeHandler)

		return () => window.removeEventListener('resize', resizeHandler)
	}, [])

	useEffect(() => {
		if (isOpened) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
			document.body.style.paddingRight = `${scrollbarWidth}px`
			document.body.classList.add('no-scroll')
			if (buttonRef.current) {
				buttonRef.current.style.marginRight = `${scrollbarWidth}px`
			}
		} else {
			document.body.style.paddingRight = ''
			document.body.classList.remove('no-scroll')
			if (buttonRef.current) {
				buttonRef.current.style.marginRight = ''
			}
		}
	}, [isOpened])

	useEffect(() => {
		if (!authStore.chatId) {
			authStore.setChatId(uuid())
		}
		async function run() {
			const id = authStore.chatId
			if (id) {
				const chatHistoryRes = await apiService.getChatHistory(id)
				const messages = chatHistoryRes.data?.messages
				setInitialMessages(messages)
			} else {
				setInitialMessages([])
			}
			setChatId(id)
		}

		run()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.chatId, authStore.setChatId])

	const shouldBeShown = useMemo(() => {
		const toFilter = [
			'/invoice-generator',
			'/make-your-1099',
			'/make-your-paystub',
			'/make-your-w2',
			'/pricing',
			'/checkout/payment',
			'/auth/sign-up',
		]

		return !toFilter.some((path) => router.asPath.startsWith(path))
	}, [router.asPath])

	const handleToggle = useCallback(() => {
		toggleIsOpened()
		if (!isDirty) setIsDirty(true)
	}, [isDirty, toggleIsOpened])

	return (
		<div className={cx('wrapper', {hide: !shouldBeShown})}>
			<ChatButton {...{isOpened, toggleIsOpened: handleToggle}} ref={buttonRef} />
			{initialMessages && isDirty && (
				<ChatWindow
					isOpened={isOpened}
					toggleIsOpened={toggleIsOpened}
					initialMessages={initialMessages}
					id={chatId}
				/>
			)}
		</div>
	)
}

export default observer(Chat)
